import React, { useRef, useState } from 'react';
import axios from 'axios';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = [
    'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'
];

export default function LogoUpload({ value, onChange, token }) {
    const fileInput = useRef();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(value || '');
    const [confirmOpen, setConfirmOpen] = useState(false);

    // Ensure preview updates when value changes (e.g., after reload)
    React.useEffect(() => {
        setPreview(value || '');
    }, [value]);

    const validateFile = (file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            return 'Invalid file type. Allowed: PNG, JPG, GIF, SVG, WEBP.';
        }
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            return `File too large (max ${MAX_SIZE_MB}MB).`;
        }
        return '';
    };

    const checkDimensions = (file) => {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = function () {
                if (img.width < 200 || img.height < 200) {
                    reject('Image must be at least 200x200 pixels.');
                } else {
                    resolve();
                }
            };
            img.onerror = () => reject('Invalid image file.');
            img.src = URL.createObjectURL(file);
        });
    };

    const handleFileChange = async (e) => {
        setError('');
        const file = e.target.files[0];
        if (!file) return;
        const err = validateFile(file);
        if (err) {
            setError(err);
            return;
        }
        try {
            await checkDimensions(file);
        } catch (dimErr) {
            setError(dimErr);
            return;
        }
        setUploading(true);
        const previousPreview = preview;
        try {
            // 1. Get presigned URL
            const { data } = await axios.post('/api/collab/school-profile/logo-upload-url', {
                fileName: file.name,
                contentType: file.type
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const { uploadUrl, s3Url } = data;
            // 2. Upload to S3
            await axios.put(uploadUrl, file, {
                headers: { 'Content-Type': file.type }
            });
            // 3. Update preview and notify parent
            setPreview(s3Url);
            if (onChange) onChange(s3Url);

            // 4. If there was a previous logo (different URL), remove it from S3
            if (previousPreview && previousPreview !== s3Url) {
                try {
                    await axios.post('/api/collab/school-profile/logo-delete', { s3Url: previousPreview }, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });
                } catch (delErr) {
                    // Non-fatal: log and continue. Keep the new logo as the canonical one.
                    // eslint-disable-next-line no-console
                    console.warn('Failed to delete previous logo from S3', delErr?.message || delErr);
                }
            }
        } catch (err) {
            setError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    // Delete logo handler
    const handleDeleteLogo = async () => {
        setError('');
        setUploading(true);
        try {
            // Call backend to delete logo from S3
            await axios.post('/api/collab/school-profile/logo-delete', { s3Url: preview }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            setPreview('');
            if (onChange) onChange('');
        } catch (err) {
            setError(err.message || 'Delete failed');
        } finally {
            setUploading(false);
            setConfirmOpen(false);
        }
    };

    return (
        <div>
            {/* Button on the left, preview on the right */}
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4, gap: 12 }}>
                <Button variant="outlined" onClick={() => fileInput.current.click()} disabled={uploading} sx={{ ml: 0, alignSelf: 'flex-start' }}>
                    {uploading ? <CircularProgress size={20} /> : (preview ? 'Update Logo' : 'Upload Logo *')}
                </Button>
                <div style={{ marginLeft: 'auto' }}>
                    {preview && (
                        <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'flex-start' }}>
                            <img src={preview} alt="Logo preview" style={{ width: 200, height: 200, objectFit: 'cover', border: '1px solid #ccc' }} />
                            <Tooltip title="Delete logo">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setConfirmOpen(true)}
                                    sx={{ position: 'absolute', top: 4, right: 4, background: '#fff', zIndex: 2 }}
                                    aria-label="delete logo"
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </div>
            <input
                type="file"
                accept={ALLOWED_TYPES.join(',')}
                style={{ display: 'none' }}
                ref={fileInput}
                onChange={handleFileChange}
            />

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Delete Logo</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this logo? This action cannot be undone and will permanently remove the file from our system.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="primary">Cancel</Button>
                    <Button onClick={handleDeleteLogo} color="error" autoFocus disabled={uploading}>Delete</Button>
                </DialogActions>
            </Dialog>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </div>
    );
}
